import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import type { PayrollRosterRowFocus } from "./types";

interface RosterMemberSummaryProps {
  memberCount: number;
  displayedCount: number;
  filteredCount: number;
  suggestionCount: number;
  departedCount: number;
  focus: PayrollRosterRowFocus;
  hasActiveFilters: boolean;
  publishedAt: string | null;
  onFocusChange: (focus: Exclude<PayrollRosterRowFocus, null>) => void;
}

interface AttentionFilterProps {
  active: boolean;
  count: number;
  label: string;
  tone: "negative" | "warning";
  onClick: () => void;
}

const AttentionFilter = ({
  active,
  count,
  label,
  tone,
  onClick,
}: AttentionFilterProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={active ? `Clear ${label} filter` : `${count} ${label}`}
    className={cn(
      "inline-flex items-center gap-1 align-middle transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      {
        "h-7 rounded-full border px-2 font-medium": active,
        "underline decoration-dotted underline-offset-2 hover:decoration-solid":
          !active,
        "border-negative-border bg-red-10 text-negative-text":
          active && tone === "negative",
        "border-gold-30 bg-gold-5 text-gold-70": active && tone === "warning",
        "text-negative-text": !active && tone === "negative",
        "text-gold-70": !active && tone === "warning",
      },
    )}
  >
    {active ? (
      <>
        <FunnelIcon className="size-3.5" />
        <span>
          Filter: {label} ({count})
        </span>
        <XMarkIcon className="size-3.5" />
      </>
    ) : (
      `${count} ${label}`
    )}
  </button>
);

export const RosterMemberSummary = ({
  memberCount,
  displayedCount,
  filteredCount,
  suggestionCount,
  departedCount,
  focus,
  hasActiveFilters,
  publishedAt,
  onFocusChange,
}: RosterMemberSummaryProps) => (
  <Typography variant="bodyW3" textColor="secondary">
    {hasActiveFilters
      ? `${filteredCount} of ${displayedCount} people`
      : `${memberCount} ${memberCount === 1 ? "person" : "people"}`}
    {(suggestionCount > 0 || focus === "missing") && (
      <>
        {" · "}
        <AttentionFilter
          active={focus === "missing"}
          count={suggestionCount}
          label="missing from roster"
          tone="negative"
          onClick={() => onFocusChange("missing")}
        />
      </>
    )}
    {(departedCount > 0 || focus === "departed") && (
      <>
        {" · "}
        <AttentionFilter
          active={focus === "departed"}
          count={departedCount}
          label="not in ServiceTitan"
          tone="warning"
          onClick={() => onFocusChange("departed")}
        />
      </>
    )}
    {publishedAt
      ? ` · last published ${dayjs(publishedAt).format("MMM D, YYYY")}`
      : ""}
  </Typography>
);
